import {isUndefined} from 'lodash';

import {compareBoundaries} from '../../../../helpers/cellsHelpers';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {AppAction} from '../../../appActions';
import {
  CellsPanelConversationsActionsEnum,
  CellsPanelMessagesActionsEnum,
  FetchCellsCachePoliciesEnum,
} from '../../../panels/cells/cellsPanelActions';
import {CellsListsActionsEnum} from '../cellsListsActions';
import {
  compareCellsListRanges,
  compareCellsListRangesMostRecent,
  isCellsListWritableForParameters,
  isEqualCellsSyncContext,
  isStrictlyIntersectingRangeBottom,
} from '../cellsListsHelpers';
import {CellsListRange, CellsListState} from './cellsListState';

/*
 * Reducer.
 */

export function cellsListReducer<T extends CellsListState<any>>(state: T, action: AppAction): T {
  switch (action.type) {
    case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED:
    case CellsPanelConversationsActionsEnum.FETCH_MORE_CONVERSATIONS_COMPLETED:
    case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED:
    case CellsPanelMessagesActionsEnum.FETCH_MORE_MESSAGES_COMPLETED: {
      const {payload} = action;
      const {parameters, cachePolicy, fetchedAt, isLoadedFromCache} = payload;
      if (
        !shouldSaveToCache(cachePolicy) ||
        isUndefined(fetchedAt) ||
        isLoadedFromCache ||
        !isCellsListWritableForParameters(state, parameters)
      ) {
        return state;
      }

      // Remember we fetched this P2 range so we know what we have in our store.
      const {topBoundary, bottomBoundary, ids} = payload;
      return addCellsListRange(state, {topBoundary, bottomBoundary, ids, fetchedAt});
    }
    case CellsListsActionsEnum.PREFETCH_CELLS_COMPLETED: {
      const {payload} = action;
      if (!isCellsListWritableForParameters(state, payload.parameters)) {
        return state;
      }

      const {topBoundary, bottomBoundary, ids, fetchedAt} = payload;
      return addCellsListRange(state, {topBoundary, bottomBoundary, ids, fetchedAt});
    }
    case CellsListsActionsEnum.SYNC_COMPLETED: {
      const {context, prevSyncedAt, syncedAt} = action.payload;
      if (!isEqualCellsSyncContext(context, state.cellsSyncContext)) {
        return state;
      }

      // Mark all previously fetched P2 ranges as being up to date.
      return {
        ...state,
        ranges: normalizeCellsListRanges(
          state.parameters.sortDirection,
          updateCellsListRangesSyncedAt(state.ranges, prevSyncedAt, syncedAt),
        ),
      };
    }

    case CellsListsActionsEnum.SYNC_FAILED:
      // Forget about all previously fetched P2 ranges.
      return {
        ...state,
        ranges: [],
      };

    default:
      return state;
  }
}

type CellsListRangeForState<T extends CellsListState<any>> = T['ranges'][0];

function addCellsListRange<T extends CellsListState<any>>(state: T, range: CellsListRangeForState<T>): T {
  return {
    ...state,
    ranges: normalizeCellsListRanges(state.parameters.sortDirection, [range, ...state.ranges]),
  };
}

/*
 * Helpers.
 */

function shouldSaveToCache(cachePolicy: FetchCellsCachePoliciesEnum) {
  return cachePolicy !== FetchCellsCachePoliciesEnum.NO_CACHE;
}

function updateCellsListRangesSyncedAt<T extends CellsListRange<unknown>>(
  ranges: ReadonlyArray<T>,
  prevSyncedAt: number,
  syncedAt: number,
): ReadonlyArray<T> {
  return ranges
    .filter((range) => {
      // If this list range was synced (or fetched) before the time interval of this sync, then this sync does not bring
      // that list forward.
      const rangeSyncedAt = range.syncedAt || range.fetchedAt;
      return rangeSyncedAt >= prevSyncedAt;
    })
    .map((range) => ({
      ...range,
      syncedAt,
    }));
}

/** Sorts ranges and eliminates old and redundant ranges. */
export function normalizeCellsListRanges<TId>(
  sortDirection: CellsSortDirectionsEnum,
  ranges: ReadonlyArray<CellsListRange<TId>>,
): ReadonlyArray<CellsListRange<TId>> {
  const newRanges: Array<CellsListRange<TId>> = [];
  let pendingOverlappingRange: CellsListRange<TId> | undefined;

  // Clone the array, since sorting happens in-place.
  const sortedRanges = ranges.slice();
  sortedRanges.sort((a, b) => compareCellsListRanges(sortDirection, a, b));

  while (sortedRanges.length > 0) {
    // Dequeue the next range.
    const range = sortedRanges.shift();

    // This should never happen, but we have to keep TS happy.
    if (!range) {
      break;
    }

    // Find the previously committed range.
    const prevRange = newRanges.length > 0 ? newRanges[newRanges.length - 1] : undefined;

    // If this range does not overlap the previously committed range at all, keep it.
    if (!prevRange || compareBoundaries(sortDirection, prevRange.bottomBoundary, range.topBoundary) < 0) {
      // If we have a pending overlapping range, commit it first, because the current range might overlap with it.
      if (pendingOverlappingRange) {
        newRanges.push(pendingOverlappingRange);
        pendingOverlappingRange = undefined;

        // Re-enqueue the current range.
        sortedRanges.unshift(range);
        continue;
      }

      newRanges.push(range);
      continue;
    }

    // If this range is completely covered by the previously committed range, discard it.
    if (!isStrictlyIntersectingRangeBottom(sortDirection, prevRange, range)) {
      continue;
    }

    // If this range is older than the pending overlapping range we found before, discard it.
    if (pendingOverlappingRange && compareCellsListRangesMostRecent(pendingOverlappingRange, range) < 0) {
      continue;
    }

    pendingOverlappingRange = range;
  }

  if (pendingOverlappingRange) {
    newRanges.push(pendingOverlappingRange);
  }

  return newRanges;
}
